import React, { useState, useEffect } from "react";
import { withRouter, useParams, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import CipHeader from "../Components/CipHeader";
import cogoToast from "cogo-toast";
import Loader from "react-loader-spinner";
import moment from "moment";
import epcisService from "../services/epcis-service";
import flstockService from "../services/flstock-service";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import Barcode from "react-barcode";
import { Table } from "ka-table";
import { DataType } from "ka-table/enums";

const getStatusStyle = (status) => {
  switch ((status || "").toLowerCase()) {
    case "complete":
      return "bg-green-100 text-green-700";
    case "warning":
      return "bg-yellow-100 text-yellow-700";
    case "failed":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const buildEventDescription = (e) => {
  if (e.type === "AggregationEvent") {
    const palletId = e.parentId ? String(e.parentId).split(":").pop() : "";
    return (
      "Packed " +
      (e.childEpcs ? e.childEpcs.length : 0) +
      " children into pallet " +
      palletId
    );
  }
  if (e.type === "ObjectEvent" && e.bizStep === "commissioning") {
    return (
      "Commissioned " +
      (e.epcs ? e.epcs.length : 0) +
      " serials"
    );
  }
  if (e.type === "ObjectEvent" && e.bizStep === "shipping") {
    const txns = (e.bizTransactions || [])
      .map((bt) => bt.type + ": " + bt.value)
      .join(" · ");
    return (
      "Shipped " +
      (e.epcs ? e.epcs.length : 0) +
      " pallet(s) · " +
      e.disposition +
      (txns ? " · " + txns : "")
    );
  }
  return (e.epcs ? e.epcs.length : 0) + " EPCs · " + e.disposition;
};


const EpcisFileDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [showAllBundles, setShowAllBundles] = useState(false);
  useEffect(() => {
    const loadFile = async () => {
      try {
        setLoading(true);
        const fileData = await epcisService.getEpcisFileById(id);
        setFile(fileData);
        if (fileData && fileData.parsed_json) {
          try {
            setParsedData(JSON.parse(fileData.parsed_json));
          } catch (parseErr) {
            setParsedData(null);
          }
        }
        setLoading(false);
      } catch (e) {
        const { hide } = cogoToast.error("Something went wrong! Please try again.", {
          hideAfter: 15,
          onClick: () => {
            hide();
          },
        });
        setLoading(false);
      }
    };
    loadFile();
  }, [id]);

  const handleDownload = async () => {
    if (!file?.blob_name) return;
    try {
      setDownloading(true);
      await flstockService.downloadEpcisFile(file.blob_name);
    } catch (e) {
      const { hide } = cogoToast.error("Error downloading file. Please try again.", {
        hideAfter: 15,
        onClick: () => { hide(); },
      });
    } finally {
      setDownloading(false);
    }
  };

  const products = parsedData && parsedData.products
    ? Object.entries(parsedData.products)
    : [];
  const events = parsedData && parsedData.events ? parsedData.events : [];
  const bundles = parsedData && Array.isArray(parsedData.bundles) ? parsedData.bundles : [];
  const visibleBundles = showAllBundles ? bundles : bundles.slice(0, 10);

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-shrink-0 z-10 relative">
        <CipHeader />
      </div>
      <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="bg-white w-11/12 sm:w-10/12 mx-auto mt-12 mb-4">
        <div className="">
          <div
            className={`inline-block customtop z-50 absolute ${
              loading ? "show" : "hidden"
            }`}
          >
            <Loader
              type="Oval"
              color="#00B2A9"
              height={50}
              width={50}
              timeout={300000}
            />
          </div>
          <div className={`inline-block w-full px-10 pb-4 border-b-2`}>

            {/* Section A — Top bar */}
            <div className="pb-4 border-b-2 pt-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => history.goBack()}
                  className="text-menuHvrColor text-sm font-semibold focus:outline-none hover:underline"
                >
                  ← Back to EPCIS Files
                </button>
                <div className="flex items-center gap-3">
                  {file && (
                    <span className="text-fogotpwsd_login text-xs font-mono max-w-xs truncate">
                      {file.original_file_name}
                    </span>
                  )}
                  {file && (
                    <span
                      className={`inline-block px-3 py-1 rounded text-xs font-semibold ${getStatusStyle(
                        file.status
                      )}`}
                    >
                      {file.status || "Unknown"}
                    </span>
                  )}
                  {file && (
                    <button
                      onClick={handleDownload}
                      disabled={downloading}
                      className={`text-white h-8 px-5 bg-header_bg rounded text-xs font-medium focus:outline-none ${downloading ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {downloading ? "Downloading..." : "Download XML"}
                    </button>
                  )}
                </div>
              </div>
              {file && file.error_message && (
                <p className="mt-2 text-xs text-red-600">{file.error_message}</p>
              )}
            </div>

            {!file && !loading && (
              <div className="text-center py-16">
                <p className="text-fogotpwsd_login text-sm">File not found.</p>
              </div>
            )}

            {file && (
              <>
                {/* Section B — Document metadata */}
                <div className="pt-5 pb-4 border-b-2">
                  <ul className="text-left">
                    <li className="inline-block text-xs leading-8 w-1/4 mb-3 align-top pr-4">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        FROM
                      </label>
                      <span className="text-fogotpwsd_login text-sm block font-semibold">
                        {file.source_owning_party_name || "—"}
                      </span>
                      {file.source_owning_party_sgln && (
                        <span className="text-fogotpwsd_login text-xs font-mono block">
                          {file.source_owning_party_sgln}
                        </span>
                      )}
                    </li>
                    <li className="inline-block text-xs leading-8 w-1/4 mb-3 align-top pr-4">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        TO
                      </label>
                      <span className="text-fogotpwsd_login text-sm block font-semibold">
                        {file.destination_owning_party_name || "—"}
                      </span>
                      {file.destination_owning_party_sgln && (
                        <span className="text-fogotpwsd_login text-xs font-mono block">
                          {file.destination_owning_party_sgln}
                        </span>
                      )}
                    </li>
                    <li className="inline-block text-xs leading-8 w-1/4 mb-3 align-top pr-4">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        DOCUMENT CREATED
                      </label>
                      <span className="text-fogotpwsd_login text-sm block">
                        {file.document_creation_time
                          ? moment(file.document_creation_time).format(
                              "MMM DD, YYYY hh:mm A"
                            )
                          : "—"}
                      </span>
                    </li>
                    <li className="inline-block text-xs leading-8 w-1/4 mb-3 align-top">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        INSTANCE ID
                      </label>
                      <span className="text-fogotpwsd_login text-xs font-mono block break-all">
                        {file.instance_identifier || "—"}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Section C — Stats */}
                <div className="pt-5 pb-4 border-b-2">
                  <ul className="text-left">
                    <li className="inline-block text-xs leading-8 w-1/4">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        Events
                      </label>
                      <span className="text-2xl font-semibold text-labelclr">
                        {file.total_events ?? 0}
                      </span>
                    </li>
                    <li className="inline-block text-xs leading-8 w-1/4">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        Total Serials
                      </label>
                      <span className="text-2xl font-semibold text-labelclr">
                        {file.total_serials ?? 0}
                      </span>
                    </li>
                    <li className="inline-block text-xs leading-8 w-1/4">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        Pallets
                      </label>
                      <span className="text-2xl font-semibold text-labelclr">
                        {file.total_pallets ?? 0}
                      </span>
                    </li>
                    <li className="inline-block text-xs leading-8 w-1/4">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        Distinct Lots
                      </label>
                      <span className="text-2xl font-semibold text-labelclr">
                        {file.total_lots ?? 0}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Section D — Business identifiers */}
                <div className="pt-5 pb-4 border-b-2">
                  <ul className="text-left">
                    <li className="inline-block text-xs leading-8 w-1/3 mb-2 pr-4">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        Shipment ID (DESADV)
                      </label>
                      <span className="text-fogotpwsd_login text-sm">
                        {file.shipment_id || "—"}
                      </span>
                    </li>
                    <li className="inline-block text-xs leading-8 w-1/3 mb-2 pr-4 align-top">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        Order ID (PO)
                      </label>
                      {file.order_id ? (
                        <Barcode
                          value={file.order_id}
                          format="CODE128"
                          height={50}
                          width={1.5}
                          fontSize={12}
                          displayValue={true}
                          margin={0}
                        />
                      ) : (
                        <span className="text-fogotpwsd_login text-sm">—</span>
                      )}
                    </li>
                    <li className="inline-block text-xs leading-8 w-1/3 mb-2">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        Document Type
                      </label>
                      <span className="inline-block bg-bodybgrnd text-menuHvrColor px-3 py-1 rounded text-xs font-semibold">
                        {(file.document_type || "").replace(/_/g, " ")}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Section E — DSCSA Transaction Statement */}
                {parsedData && parsedData.dscsa && (
                  <div className="pt-5 pb-4 border-b-2">
                    <label className="text-labelclr font-semibold text-sm tracking-wider block mb-3">
                      DSCSA TRANSACTION STATEMENT
                    </label>
                    {parsedData.dscsa.legalNotice && (
                      <p className="text-fogotpwsd_login text-xs italic mb-3 leading-5">
                        {parsedData.dscsa.legalNotice}
                      </p>
                    )}
                    <span
                      className={`inline-block px-3 py-1 rounded text-xs font-semibold ${
                        parsedData.dscsa.affirmed
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {parsedData.dscsa.affirmed ? "✓ Affirmed" : "Not Affirmed"}
                    </span>
                  </div>
                )}

                {/* Section F — Products table */}
                {products.length > 0 && (
                  <div className="pt-5 pb-4 border-b-2">
                    <label className="text-labelclr font-semibold text-sm tracking-wider block mb-3">
                      PRODUCTS
                    </label>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-bodybgrnd text-labelclr text-xs uppercase tracking-wider">
                            <th className="px-4 py-3 text-left font-semibold">NDC</th>
                            <th className="px-4 py-3 text-left font-semibold">Drug</th>
                            <th className="px-4 py-3 text-left font-semibold">Manufacturer</th>
                            <th className="px-4 py-3 text-left font-semibold">Dosage Form</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map(([epcClass, info], i) => {
                            const parts = epcClass.split(".");
                            const ndc =
                              parts.length >= 2
                                ? parts[parts.length - 2]
                                : epcClass.split(":").pop();
                            return (
                              <tr key={i} className="border-b text-sm text-fogotpwsd_login hover:bg-gray-50">
                                <td className="px-4 py-3 font-mono text-xs">
                                  {info.additionalTradeItemIdentificationTypeCode === "FDA_NDC_11"
                                    ? info.additionalTradeItemIdentification
                                    : ndc}
                                </td>
                                <td className="px-4 py-3">
                                  {[
                                    info.regulatedProductName,
                                    info.strengthDescription,
                                    info.dosageFormType
                                  ].filter(Boolean).join(" ") || "—"}
                                </td>
                                <td className="px-4 py-3">{info.manufacturerOfTradeItemPartyName || "—"}</td>
                                <td className="px-4 py-3">{info.dosageFormType || "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Section G — Event Timeline */}
                <div className="pt-5 pb-4 border-b-2">
                  <label className="text-labelclr font-semibold text-sm tracking-wider block mb-3">
                    EVENT TIMELINE
                  </label>
                  {events.length === 0 ? (
                    <p className="text-fogotpwsd_login text-sm">No events found.</p>
                  ) : (
                    <div className="ka-table-wrapper">
                      <Table
                        columns={[
                          { key: "badge", title: "Event", dataType: DataType.String, style: { width: 140 } },
                          { key: "description", title: "Description", dataType: DataType.String },
                          { key: "lot", title: "Lot", dataType: DataType.String, style: { width: 140 } },
                          { key: "expiry", title: "Expiration", dataType: DataType.String, style: { width: 140 } },
                          { key: "eventTime", title: "Time", dataType: DataType.String, style: { width: 190 } },
                        ]}
                        data={events.map((e, i) => ({
                          id: i,
                          badge: e.bizStep || e.type,
                          description: buildEventDescription(e),
                          lot: e.lot || null,
                          expiry: e.expiry || null,
                          eventTime: e.eventTime || null,
                        }))}
                        rowKeyField={"id"}
                        childComponents={{
                          cellText: {
                            content: (props) => {
                              const { column, rowData } = props;
                              if (column.key === "badge") {
                                return (
                                  <span className="inline-block px-3 py-1 rounded text-xs font-semibold bg-bodybgrnd text-menuHvrColor capitalize">
                                    {rowData.badge}
                                  </span>
                                );
                              }
                              if (column.key === "lot") {
                                return rowData.lot
                                  ? <span className="font-mono text-xs">{rowData.lot}</span>
                                  : <span className="text-gray-400">—</span>;
                              }
                              if (column.key === "expiry") {
                                return rowData.expiry
                                  ? moment(rowData.expiry).format("MMM DD, YYYY")
                                  : <span className="text-gray-400">—</span>;
                              }
                              if (column.key === "eventTime") {
                                return rowData.eventTime
                                  ? moment(rowData.eventTime).format("MMM DD, YYYY hh:mm A")
                                  : "—";
                              }
                              return undefined;
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Section H — Serialized Bundles */}
                {bundles.length > 0 && (
                  <div className="pt-5 pb-4 border-b-2">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-labelclr font-semibold text-sm tracking-wider block">
                        SERIALIZED BUNDLES ({bundles.length})
                      </label>
                      {bundles.length > 10 && (
                        <button
                          onClick={() => setShowAllBundles((v) => !v)}
                          className="text-menuHvrColor text-xs font-semibold focus:outline-none hover:underline"
                        >
                          {showAllBundles ? "Show first 10" : `Show all ${bundles.length}`}
                        </button>
                      )}
                    </div>

                    <div className="flex flex-col gap-3">
                      {visibleBundles.map((b, i) => {
                        const productMap = parsedData?.products || {};
                        const info = productMap[b.epcClass] || {};
                        const parts = (b.epcClass || "").split(".");
                        const ndc = parts.length >= 2 ? parts[parts.length - 2] : (b.epcClass || "").split(":").pop();
                        const fullNdc =
                          info.additionalTradeItemIdentificationTypeCode === "FDA_NDC_11"
                            ? info.additionalTradeItemIdentification
                            : ndc;
                        return (
                        <div
                          key={i}
                          className="border rounded-lg px-4 py-3"
                        >
                          <div className="flex flex-wrap items-center gap-2 text-xs text-fogotpwsd_login mb-2">
                            <span className="font-semibold text-labelclr">Bundle</span>
                            <span className="font-mono">
                              SGTIN: {b.epcClass || (b.gtin && b.sn ? `${b.gtin}.${b.sn}` : "—")}
                            </span>
                            <span>·</span>
                            <span>Lot: {b.lot || "—"}</span>
                            <span>·</span>
                            <span>Exp: {b.expiry ? moment(b.expiry).format("MM/DD/YYYY") : "—"}</span>
                          </div>

                          <div className="flex flex-wrap gap-1.5 mb-1.5">
                            <span
                              className="inline-block px-2.5 py-1 rounded text-xs font-mono font-semibold"
                              style={{ backgroundColor: "#FAEEDA", color: "#633806" }}
                            >
                              GTIN {b.gtin || "—"}
                            </span>
                            <span
                              className="inline-block px-2.5 py-1 rounded text-xs font-mono font-semibold"
                              style={{ backgroundColor: "#E6F1FB", color: "#0C447C" }}
                            >
                              NDC {fullNdc || "—"}
                            </span>
                            {b.drug && (
                              <span
                                className="inline-block px-2.5 py-1 rounded text-xs font-semibold"
                                style={{ backgroundColor: "#EEEDFE", color: "#3C3489" }}
                              >
                                Drug {[b.drug, info.strengthDescription].filter(Boolean).join(" ")}
                              </span>
                            )}
                            {b.manufacturer && (
                              <span
                                className="inline-block px-2.5 py-1 rounded text-xs font-semibold"
                                style={{ backgroundColor: "#E1F5EE", color: "#085041" }}
                              >
                                Mfr {b.manufacturer}
                              </span>
                            )}
                            {b.dosageForm && (
                              <span
                                className="inline-block px-2.5 py-1 rounded text-xs font-semibold"
                                style={{ backgroundColor: "#FBEAF0", color: "#72243E" }}
                              >
                                Form {b.dosageForm}
                              </span>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-1.5">
                            <span
                              className="inline-block px-2.5 py-1 rounded text-xs font-mono font-semibold"
                              style={{ backgroundColor: "#FAEEDA", color: "#633806" }}
                            >
                              SN {b.sn || "—"}
                            </span>
                            <span
                              className="inline-block px-2.5 py-1 rounded text-xs font-semibold"
                              style={{ backgroundColor: "#FAEEDA", color: "#633806" }}
                            >
                              Lot {b.lot || "—"}
                            </span>
                            <span
                              className="inline-block px-2.5 py-1 rounded text-xs font-semibold"
                              style={{ backgroundColor: "#EAF3DE", color: "#27500A" }}
                            >
                              Exp {b.expiry ? moment(b.expiry).format("MM/DD/YYYY") : "—"}
                            </span>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default withRouter(
  connect(
    () => ({}),
    (dispatch) => {
      return bindActionCreators({}, dispatch);
    }
  )(EpcisFileDetails)
);